import {
    mxCell,
    mxClient,
    mxClipboard,
    mxConstants,
    mxEvent,
    mxEventObject,
    mxEventSource,
    mxKeyHandler,
    mxOutline,
    mxPoint,
    mxRectangle,
    mxResources,
    mxStackLayout,
    mxUtils,
} from './mxGraphFactory';
import {Graph, HoverIcons, UPDATE_CELL_VALUE} from './Graph';
import {Actions} from './Actions';
import {PopupMenu} from './PopupMenu';
import {toWorkflowDefinitionGeometry, toTaskProcessors, toTemplateTaskNames, toWorkflowDefinition, toWorkflowStartPage} from './workflow';
import {deepCopy} from './helper';
import isEmpty from 'lodash/isEmpty';
import {api} from '../Common/fetchWithTimeout';
import {Sidebar} from './Sidebar';
import {Toolbar} from './Toolbar';
import {stringifyJSON} from '../Common/json';
import {createLogger} from '../Common/logger';
import {v4 as uuid} from 'uuid';
import {DraggablePane} from "../Common/DraggablePane";
import {mapServerFieldErrorsToGraphErrors} from './serverValidationErrors';

const LOGGER = createLogger("GraphEditorUi");

export const EditorUi = function (editor, container, currentWorkflowDefinition, fullMode = true) {
    mxEventSource.call(this);

    this.currentWorkflowDefinition = currentWorkflowDefinition;
    this.fullMode = fullMode;
    this.destroyFunctions = [];
    this.container = container;
    this.editor = editor;
    this.createGraphContainers();
    this.editor.initGraph(this.diagramContainer);
    this.actions = new Actions(this);
    this.popupMenu = new PopupMenu(this);
    this.createUi();
    this.refresh();

    const graph = this.editor.graph;

    this.hoverIcons = new HoverIcons(graph);

    if (this.editor.editable) {
        this.hoverIcons.init();
    }

    if (graph.useCssTransforms) {
        this.lazyZoomDelay = 0;
    }

    // And uses built-in context menu while editing
    if (this.editor.editable) {
        const textEditing = mxUtils.bind(this, function (evt) {
            if (evt == null) {
                evt = window.event;
            }

            return (this.isSelectionAllowed(evt) || graph.isEditing());
        });

        if (mxClient.IS_IE && (typeof (document.documentMode) === 'undefined' || document.documentMode < 9)) {
            mxEvent.addListener(this.diagramContainer, 'contextmenu', textEditing);
        } else {
            // Allows browser context menu outside of diagram
            this.diagramContainer.oncontextmenu = textEditing;
        }
    } else {
        graph.panningHandler.usePopupTrigger = false;
    }

    const drawPane = graph.view.getDrawPane();
    if (drawPane != null && drawPane.ownerSVGElement != null) {
        drawPane.ownerSVGElement.style.position = 'absolute';
    }

    mxEvent.addListener(this.diagramContainer, 'mousemove', mxUtils.bind(this, function (evt) {
        const off = mxUtils.getOffset(this.diagramContainer);

        if (mxEvent.getClientX(evt) - off.x - this.diagramContainer.clientWidth > 0 ||
            mxEvent.getClientY(evt) - off.y - this.diagramContainer.clientHeight > 0) {
            this.diagramContainer.setAttribute('title', mxResources.get('panTooltip'));
        } else {
            this.diagramContainer.removeAttribute('title');
        }
    }));

    let spaceKeyPressed = false;

    const hoverIconsIsResetEvent = this.hoverIcons.isResetEvent;
    this.hoverIcons.isResetEvent = function (evt, allowShift) {
        return spaceKeyPressed || hoverIconsIsResetEvent.apply(this, arguments);
    };

    this.keydownHandler = mxUtils.bind(this, function (evt) {
        if (evt.which === 32 /* Space */) {
            spaceKeyPressed = true;
            this.hoverIcons.reset();
            graph.container.style.cursor = 'move';

            // Disables scroll after space keystroke with scrollbars
            if (!graph.isEditing() && mxEvent.getSource(evt) === graph.container) {
                mxEvent.consume(evt);
            }
        } else if (!mxEvent.isConsumed(evt) && evt.keyCode === 27 /* Escape */) {
            mxUtils.clearSelection();
        }
    });
    mxEvent.addListener(document, 'keydown', this.keydownHandler);

    this.keyupHandler = mxUtils.bind(this, function (evt) {
        graph.container.style.cursor = '';
        spaceKeyPressed = false;
    });
    mxEvent.addListener(document, 'keyup', this.keyupHandler);

    // Forces panning for middle and right mouse buttons
    const panningHandlerIsForcePanningEvent = graph.panningHandler.isForcePanningEvent;
    graph.panningHandler.isForcePanningEvent = function (me) {
        // Ctrl+left button is reported as right button in FF on Mac
        return panningHandlerIsForcePanningEvent.apply(this, arguments) ||
            spaceKeyPressed || (mxEvent.isMouseEvent(me.getEvent()) &&
                (this.usePopupTrigger || !mxEvent.isPopupTrigger(me.getEvent())) &&
                ((!mxEvent.isControlDown(me.getEvent()) &&
                        mxEvent.isRightMouseButton(me.getEvent())) ||
                    mxEvent.isMiddleMouseButton(me.getEvent())));
    };

    // Ctrl/Cmd+Enter applies editing value except in Safari where Ctrl+Enter creates
    // a new line (while Enter creates a new paragraph and Shift+Enter stops)
    const cellEditorIsStopEditingEvent = graph.cellEditor.isStopEditingEvent;
    graph.cellEditor.isStopEditingEvent = function (evt) {
        return cellEditorIsStopEditingEvent.apply(this, arguments) ||
            (evt.keyCode === 13 && ((!mxClient.IS_SF && mxEvent.isControlDown(evt)) ||
                (mxClient.IS_MAC && mxEvent.isMetaDown(evt)) ||
                (mxClient.IS_SF && mxEvent.isShiftDown(evt))));
    };

    // Enables scrollbars and sets cursor style for the container
    graph.container.setAttribute('tabindex', '0');
    graph.container.style.cursor = 'default';
    graph.container.style.touchAction = 'none';

    // Configures automatic expand on mouseover
    graph.popupMenuHandler.autoExpand = true;

    // Installs context menu
    if (this.editor.editable && this.popupMenu != null) {
        graph.popupMenuHandler.factoryMethod = mxUtils.bind(this, function (menu, cell, evt) {
            this.popupMenu.createPopupMenu(menu, cell, evt);
        });
    }

    // Hides context menu
    mxEvent.addGestureListeners(document, mxUtils.bind(this, function (evt) {
        graph.popupMenuHandler.hideMenu();
    }));

    // Create handler for key events
    this.keyHandler = this.createKeyHandler(this.editor);

    // Getter for key handler
    this.getKeyHandler = () => this.keyHandler;

    const removeCondition = (model, cell) => {
        const route = deepCopy(cell.getValue());
        route.routingType = 'UnConditional';
        route.condition = null;
        model.setValue(cell, route);
        graph.removeCellOverlay(cell, graph.conditionsOverlay());
    }

    const updateRoutes = cell => {
        const model = graph.getModel();

        if (model.isEdge(cell)) {
            if (cell.isConditional()) {
                graph.addCellOverlay(cell, graph.conditionsOverlay());
            } else {
                removeCondition(model, cell);
                updateRoutes(cell.source);
            }
        } else {
            const outgoingEdges = model.getEdges(cell, false, true, true);

            if (cell.isSplit() && outgoingEdges.length > 1) {
                if (cell.isStart()) {
                    graph.addCellOverlay(cell, graph.forkOverlay(-15, -34));
                } else {
                    graph.addCellOverlay(cell, graph.forkOverlay());
                }

                outgoingEdges.forEach(edge => {
                    removeCondition(model, edge);
                });
            } else {
                const task = deepCopy(cell.getValue());
                task.splitType = cell.value?.splitType;
                model.setValue(cell, task);
                graph.removeCellOverlay(cell, graph.forkOverlay());

                const allUnconditional = outgoingEdges.every(edge => !edge.isConditional());
                if (allUnconditional) {
                    outgoingEdges.forEach((edge, index) => {
                        if (index > 0) {
                            const route = deepCopy(edge.getValue());
                            route.routingType = 'Conditional';
                            model.setValue(edge, route);
                            graph.addCellOverlay(edge, graph.conditionsOverlay());
                        }
                    });
                }
            }

            if (cell.isJoin()) {
                const task = cell.getValue();
                const passThroughJoin = cell.isStart() && !cell.isSplit() &&
                    !task.type && !task.decisionTask && !task.parallelTask &&
                    cell.getParent()?.getValue()?.startTask !== cell.id;
                if (cell.isEnd() || passThroughJoin) {
                    graph.addCellOverlay(cell, graph.joinOverlay(15, -34));
                } else {
                    graph.addCellOverlay(cell, graph.joinOverlay());
                }

                model.getEdges(cell, true, false, true).forEach(edge => {
                    if (edge.source.isSplit()) {
                        graph.removeCells([edge]);
                    }
                });
            } else {
                graph.removeCellOverlay(cell, graph.joinOverlay());
            }
        }
    }

    graph.addListener(UPDATE_CELL_VALUE, function (sender, evt) {
        const model = graph.getModel();
        model.beginUpdate();
        try {
            updateRoutes(evt.getProperty('cell'));
        } finally {
            model.endUpdate();
        }
    });

    graph.addListener(mxEvent.CELLS_REMOVED, function (sender, evt) {
        const model = graph.getModel();
        model.beginUpdate();
        try {
            evt.getProperty('cells').forEach(cell => {
                if (cell?.isMap()) {
                    const task = cell.getValue();
                    if (task.itemProcessorId) {
                        model.getRoot().children.find(layer => {
                            if (layer?.id === task.itemProcessorId) {
                                model.remove(layer);
                            }
                        });
                    }
                }
                updateRoutes(cell);
            });
        } finally {
            model.endUpdate();
        }
    });

    const ui = this;
    graph.addListener(mxEvent.CELLS_ADDED, function (sender, evt) {
        const model = graph.getModel();
        model.beginUpdate();
        try {
            evt.getProperty('cells').forEach(cell => {
                if (cell.isMap()) {
                    const task = deepCopy(cell.getValue());
                    if (!task.itemProcessorId) {
                        const itemProcessor = new mxCell();
                        itemProcessor.id = uuid();
                        itemProcessor.setVisible(false);

                        model.getRoot().insert(itemProcessor);

                        const start = graph.insertVertex(itemProcessor, uuid(), {
                            name: `${cell.getValue().name} - ${mxResources.get('start')}`,
                            splitType: 'Or',
                            inputParameters: null,
                        }, 0, 0, 30, 30, 'start');

                        model.setValue(itemProcessor, {
                            mapTaskId: cell.id,
                            startTask: start.id,
                        });

                        task.itemProcessorId = itemProcessor.id;
                        model.setValue(cell, task);
                    }

                    graph.addCellOverlay(cell,
                        graph.mapOverlay(cell, task.itemProcessorId,
                            () => {
                                ui.resetScrollbarsTopLeft();
                            },
                            (parent, parentViewport) => {
                                ui.restoreMapViewport(parentViewport);
                            })
                    );
                }
                updateRoutes(cell);
            });
        } finally {
            model.endUpdate();
        }
    });

    // Updates the editor UI after the window has been resized or the orientation changes
    // Timeout is workaround for old IE versions which have a delay for DOM client sizes.
    // Should not use delay > 0 to avoid handle multiple repaints during window resize
    this.resizeHandler = mxUtils.bind(this, function () {
        window.setTimeout(mxUtils.bind(this, function () {
            if (this.editor.graph != null) {
                this.refreshSplitterAfterViewportChange();
            }
        }), 0);
    });

    mxEvent.addListener(window, 'resize', this.resizeHandler);

    /**
     * Sets the initial scrollbar locations after a file was loaded.
     */
    this.editor.addListener('resetGraphView', mxUtils.bind(this, function () {
        this.initializeGraphView();
    }));

    this.diagramOutline && new mxOutline(this.editor.graph, this.diagramOutline);

    // Resets UI, updates action and menu states
    this.editor.resetGraph();
    this.init();
};

// Extends mxEventSource
mxUtils.extend(EditorUi, mxEventSource);

EditorUi.prototype.createGraphContainers = function () {
    const createDiv = classname => {
        const el = document.createElement('div');
        el.className = classname;
        return el;
    };

    this.toolbarContainer = createDiv('geToolbarContainer');
    this.toolbarContainer.classList.add('ui', 'basic', 'fitted', 'segment');
    this.container.appendChild(this.toolbarContainer);

    this.diagramContainer = createDiv('geDiagramContainer');
    this.diagramContainer.style.right = '0px';
    this.container.appendChild(this.diagramContainer);

    if (this.fullMode && !(mxClient.IS_TOUCH || mxClient.IS_POINTER)) {
        this.diagramOutline = createDiv('geDiagramOutline');
        this.diagramOutline.style.cssText = 'z-index:1;position:sticky;overflow:hidden;top:16px;left:calc(100% - 176px);right:16px;margin-left:auto;margin-right:16px;width:160px;height:100px;max-height:calc(100% - 32px);box-sizing:border-box;background:transparent;border-style:solid;border-color:lightgray;';
        this.diagramContainer.appendChild(this.diagramOutline);
    }
};

/**
 * Specifies the height of the toolbar. Default is 36.
 */
EditorUi.prototype.toolbarHeight = 42;

/**
 * Specifies the height of the palette. Default is 36.
 */
EditorUi.prototype.paletteHeight = 42;

/**
 * Specifies the size of the split bar.
 */
EditorUi.prototype.splitSize = 10;

EditorUi.prototype.splitMinDiagramHeight = 120;

EditorUi.prototype.splitViewportMargin = 16;

EditorUi.prototype.splitPosition = 0;

EditorUi.prototype.mobileInitialSplitRatio = 0.38;

/**
 * Installs the listeners to update the action states.
 */
EditorUi.prototype.init = function () {
    /**
     * Keypress starts immediate editing on selection cell
     */
    const graph = this.editor.graph;

    mxEvent.addListener(graph.container, 'keydown', mxUtils.bind(this, function (evt) {
        this.onKeyDown(evt);
    }));
    mxEvent.addListener(graph.container, 'keypress', mxUtils.bind(this, function (evt) {
        this.onKeyPress(evt);
    }));

    // Updates action states
    this.addUndoListener();
    this.addBeforeUnloadListener();

    graph.getSelectionModel().addListener(mxEvent.CHANGE, mxUtils.bind(this, function () {
        this.updateActionStates();
    }));

    graph.getModel().addListener(mxEvent.CHANGE, mxUtils.bind(this, function () {
        this.updateActionStates();
    }));

    // Changes action states after change of default parent
    const graphSetDefaultParent = graph.setDefaultParent;
    const ui = this;

    graph.setDefaultParent = function () {
        graphSetDefaultParent.apply(this, arguments);
        ui.updateActionStates();
    };

    this.updateActionStates();
    this.initClipboard();
    this.initCanvas();
};

/**
 * Returns true if the given event should start editing. This implementation returns true.
 */
EditorUi.prototype.onKeyDown = function (evt) {
    const graph = this.editor.graph;

    // Tab selects next cell
    if (evt.which === 9 && graph.isEnabled() && !mxEvent.isAltDown(evt)) {
        if (graph.isEditing()) {
            graph.stopEditing(false);
        } else {
            graph.selectCell(!mxEvent.isShiftDown(evt));
        }

        mxEvent.consume(evt);
    }
};

/**
 * Returns true if the given event should start editing. This implementation returns true.
 */
EditorUi.prototype.onKeyPress = function (evt) {
    if (!this.editor.editable) {
        return false;
    }

    const graph = this.editor.graph;

    // KNOWN: Focus does not work if label is empty in quirks mode
    if (this.isImmediateEditingEvent(evt) && !graph.isEditing() && !graph.isSelectionEmpty() && evt.which !== 0 &&
        !mxEvent.isAltDown(evt) && !mxEvent.isControlDown(evt) && !mxEvent.isMetaDown(evt)) {
        graph.escape();
        graph.startEditing();

        // Workaround for FF where char is lost if cursor is placed before char
        if (mxClient.IS_FF) {
            const ce = graph.cellEditor;
            const textarea = ce?.textarea;

            if (textarea == null) {
                return false;
            }

            textarea.innerHTML = String.fromCharCode(evt.which);

            // Moves cursor to end of textarea
            const range = document.createRange();
            range.selectNodeContents(textarea);
            range.collapse(false);
            const sel = window.getSelection();
            sel.removeAllRanges();
            sel.addRange(range);
        }
    }
};

/**
 * Returns true if the given event should start editing. This implementation returns true.
 */
EditorUi.prototype.isImmediateEditingEvent = function (evt) {
    return true;
};

/**
 * Hook for allowing selection and context menu for certain events.
 */
EditorUi.prototype.updatePasteActionStates = function () {
    const graph = this.editor.graph;
    const paste = this.actions.get('paste');
    const pasteHere = this.actions.get('pasteHere');

    paste.setEnabled(!mxClipboard.isEmpty() && graph.isEnabled() && !graph.isCellLocked(graph.getDefaultParent()));
    pasteHere.setEnabled(paste.isEnabled());
};

/**
 * Hook for allowing selection and context menu for certain events.
 */
EditorUi.prototype.initClipboard = function () {
    const ui = this;

    const mxClipboardCut = mxClipboard.cut;
    mxClipboard.cut = function (graph) {
        mxClipboardCut.apply(this, arguments);
        ui.updatePasteActionStates();
    };

    const mxClipboardCopy = mxClipboard.copy;
    mxClipboard.copy = function (graph) {
        mxClipboardCopy.apply(this, arguments);
        ui.updatePasteActionStates();
    };

    const mxClipboardPaste = mxClipboard.paste;
    mxClipboard.paste = function (graph) {
        let result = mxClipboardPaste.apply(this, arguments);

        ui.updatePasteActionStates();

        return result;
    };

    // Overrides cell editor to update paste action state
    const cellEditorStartEditing = this.editor.graph.cellEditor.startEditing;

    this.editor.graph.cellEditor.startEditing = function () {
        cellEditorStartEditing.apply(this, arguments);
        ui.updatePasteActionStates();
    };

    const cellEditorStopEditing = this.editor.graph.cellEditor.stopEditing;

    this.editor.graph.cellEditor.stopEditing = function (cell, trigger) {
        cellEditorStopEditing.apply(this, arguments);
        ui.updatePasteActionStates();
    };

    this.updatePasteActionStates();
};

/**
 * Initializes the infinite canvas.
 */
EditorUi.prototype.lazyZoomDelay = 20;

/**
 * Initializes the infinite canvas.
 */
EditorUi.prototype.initCanvas = function () {
    const graph = this.editor.graph;
    graph.timerAutoScroll = true;

    /**
     * Returns the padding for pages in page view with scrollbars.
     */
    graph.getPagePadding = function () {
        return new mxPoint(Math.max(0, Math.round((graph.container.offsetWidth - 34) / graph.view.scale)),
            Math.max(0, Math.round((graph.container.offsetHeight - 34) / graph.view.scale)));
    };

    // Fits the number of background pages to the graph
    graph.view.getBackgroundPageBounds = function () {
        const layout = this.graph.getPageLayout();
        const page = this.graph.getPageSize();

        return new mxRectangle(this.scale * (this.translate.x + layout.x * page.width),
            this.scale * (this.translate.y + layout.y * page.height),
            this.scale * layout.width * page.width,
            this.scale * layout.height * page.height);
    };

    graph.getPreferredPageSize = function () {
        const pages = this.getPageLayout();
        const size = this.getPageSize();

        return new mxRectangle(0, 0, pages.width * size.width, pages.height * size.height);
    };

    /**
     * Guesses autoTranslate to avoid another repaint (see below).
     * Works if only the scale of the graph changes or if pages
     * are visible and the visible pages do not change.
     */
    const graphViewValidate = graph.view.validate;
    graph.view.validate = function () {
        if (this.graph.container != null && mxUtils.hasScrollbars(this.graph.container)) {
            const pad = this.graph.getPagePadding();
            const size = this.graph.getPageSize();

            // Updating scrollbars here causes flickering in quirks and is not needed
            // if zoom method is always used to set the current scale on the graph.
            this.translate.x = pad.x - (this.x0 || 0) * size.width;
            this.translate.y = pad.y - (this.y0 || 0) * size.height;
        }

        graphViewValidate.apply(this, arguments);
    };

    const graphSizeDidChange = graph.sizeDidChange;
    graph.sizeDidChange = function () {
        if (this.container != null && mxUtils.hasScrollbars(this.container)) {
            const pages = this.getPageLayout();
            const pad = this.getPagePadding();
            const size = this.getPageSize();

            // Updates the minimum graph size
            const minw = Math.ceil(2 * pad.x + pages.width * size.width);
            const minh = Math.ceil(2 * pad.y + pages.height * size.height);

            const min = graph.minimumGraphSize;

            // LATER: Fix flicker of scrollbar size in IE quirks mode
            // after delayed call in window.resize event handler
            if (min == null || min.width !== minw || min.height !== minh) {
                graph.minimumGraphSize = new mxRectangle(0, 0, minw, minh);
            }

            // Updates auto-translate to include padding and graph size
            const dx = pad.x - pages.x * size.width;
            const dy = pad.y - pages.y * size.height;

            if (!this.autoTranslate && (this.view.translate.x !== dx || this.view.translate.y !== dy)) {
                this.autoTranslate = true;
                this.view.x0 = pages.x;
                this.view.y0 = pages.y;

                // NOTE: THIS INVOKES THIS METHOD AGAIN. UNFORTUNATELY THERE IS NO WAY AROUND THIS SINCE THE
                // BOUNDS ARE KNOWN AFTER THE VALIDATION AND SETTING THE TRANSLATE TRIGGERS A REVALIDATION.
                // SHOULD MOVE TRANSLATE/SCALE TO VIEW.
                const tx = graph.view.translate.x;
                const ty = graph.view.translate.y;
                graph.view.setTranslate(dx, dy);

                // LATER: Fix rounding errors for small zoom
                graph.container.scrollLeft += Math.round((dx - tx) * graph.view.scale);
                graph.container.scrollTop += Math.round((dy - ty) * graph.view.scale);

                this.autoTranslate = false;

                return;
            }

            graphSizeDidChange.apply(this, arguments);
        }
    };

    // Accumulates the zoom factor while the rendering is taking place
    // so that not the complete sequence of zoom steps must be painted
    graph.updateZoomTimeout = null;
    graph.cumulativeZoomFactor = 1;

    let cursorPosition = null;

    graph.lazyZoom = function (zoomIn) {
        if (this.updateZoomTimeout != null) {
            window.clearTimeout(this.updateZoomTimeout);
        }

        // Switches to 1% zoom steps below 15%
        // Lower bound depdends on rounding below
        if (zoomIn) {
            if (this.view.scale * this.cumulativeZoomFactor < 0.15) {
                this.cumulativeZoomFactor = (this.view.scale + 0.01) / this.view.scale;
            } else {
                // Uses to 5% zoom steps for better grid rendering in webkit
                // and to avoid rounding errors for zoom steps
                this.cumulativeZoomFactor *= this.zoomFactor;
                this.cumulativeZoomFactor = Math.round(this.view.scale * this.cumulativeZoomFactor * 20) / 20 / this.view.scale;
            }
        } else {
            if (this.view.scale * this.cumulativeZoomFactor <= 0.15) {
                this.cumulativeZoomFactor = (this.view.scale - 0.01) / this.view.scale;
            } else {
                // Uses to 5% zoom steps for better grid rendering in webkit
                // and to avoid rounding errors for zoom steps
                this.cumulativeZoomFactor /= this.zoomFactor;
                this.cumulativeZoomFactor = Math.round(this.view.scale * this.cumulativeZoomFactor * 20) / 20 / this.view.scale;
            }
        }

        this.cumulativeZoomFactor = Math.max(0.01, Math.min(this.view.scale * this.cumulativeZoomFactor, 160) / this.view.scale);

        this.updateZoomTimeout = window.setTimeout(mxUtils.bind(this, this.flushLazyZoom), this.lazyZoomDelay);
    };

    graph.flushLazyZoom = function () {
        if (this.updateZoomTimeout != null) {
            window.clearTimeout(this.updateZoomTimeout);
            const offset = mxUtils.getOffset(graph.container);
            let dx = 0;
            let dy = 0;

            if (cursorPosition != null) {
                dx = graph.container.offsetWidth / 2 - cursorPosition.x + offset.x;
                dy = graph.container.offsetHeight / 2 - cursorPosition.y + offset.y;
            }

            const prev = this.view.scale;
            this.zoom(this.cumulativeZoomFactor);
            const s = this.view.scale;

            if (s !== prev) {
                if (mxUtils.hasScrollbars(graph.container) && (dx !== 0 || dy !== 0)) {
                    graph.container.scrollLeft -= dx * (this.cumulativeZoomFactor - 1);
                    graph.container.scrollTop -= dy * (this.cumulativeZoomFactor - 1);
                }
            }

            this.cumulativeZoomFactor = 1;
            this.updateZoomTimeout = null;
        }
    };

    mxEvent.addMouseWheelListener(mxUtils.bind(this, function (evt, up) {
        // Ctrl+wheel (or pinch on touchpad) is a native browser zoom event is OS X
        // LATER: Add support for zoom via pinch on trackpad for Chrome in OS X
        if (graph.isZoomWheelEvent(evt)) {
            let source = mxEvent.getSource(evt);

            while (source != null) {
                if (source === graph.container) {
                    cursorPosition = new mxPoint(mxEvent.getClientX(evt), mxEvent.getClientY(evt));
                    graph.lazyZoom(up);
                    mxEvent.consume(evt);

                    return;
                }

                source = source.parentNode;
            }
        }
    }));
};

/**
 * Creates a temporary graph instance for rendering off-screen content.
 */
EditorUi.prototype.createTemporaryGraph = function (stylesheet) {
    const graph = new Graph(document.createElement('div'), stylesheet);
    graph.resetViewOnRootChange = false;
    graph.setConnectable(false);
    graph.gridEnabled = false;
    graph.autoScroll = false;
    graph.setTooltips(false);
    graph.setEnabled(false);

    // Container must be in the DOM for correct HTML rendering
    graph.container.style.visibility = 'hidden';
    graph.container.style.position = 'absolute';
    graph.container.style.overflow = 'hidden';
    graph.container.style.height = '1px';
    graph.container.style.width = '1px';

    return graph;
};

/**
 * Hook for allowing selection and context menu for certain events.
 */
EditorUi.prototype.isSelectionAllowed = function (evt) {
    return mxEvent.getSource(evt).nodeName === 'SELECT' || mxEvent.getSource(evt).nodeName === 'INPUT';
};

/**
 * Installs dialog if browser window is closed without saving
 * This must be disabled during save and image export.
 */
EditorUi.prototype.addBeforeUnloadListener = function () {
    this.beforeUnloadHandler = mxUtils.bind(this, function (event) {
        return this.onBeforeUnload(event);
    });
    mxEvent.addListener(window, 'beforeunload', this.beforeUnloadHandler);
};

/**
 * Sets the onbeforeunload for the application
 */
EditorUi.prototype.onBeforeUnload = function (event) {
    if (this.editor.modified && this.editor.editable) {
        const message = mxResources.get('allChangesLost');

        event?.preventDefault?.();

        if (event != null) {
            event.returnValue = message;
        }

        return message;
    }
};

/**
 * Returns the URL for a copy of this editor with no state.
 */
EditorUi.prototype.redo = function () {
    try {
        const graph = this.editor.graph;

        if (graph.isEditing()) {
            document.execCommand('redo', false, null);
        } else {
            this.editor.undoManager.redo();
        }
    } catch (e) {
        // ignore all errors
    }
};

/**
 * Returns the URL for a copy of this editor with no state.
 */
EditorUi.prototype.undo = function () {
    try {
        const graph = this.editor.graph;

        if (graph.isEditing()) {
            // Stops editing and executes undo on graph if native undo
            // does not affect current editing value
            const value = graph.cellEditor.textarea.innerHTML;
            document.execCommand('undo', false, null);

            if (value === graph.cellEditor.textarea.innerHTML) {
                graph.stopEditing(true);
                this.editor.undoManager.undo();
            }
        } else {
            this.editor.undoManager.undo();
        }
    } catch (e) {
        // ignore all errors
    }
};

/**
 * Returns the URL for a copy of this editor with no state.
 */
EditorUi.prototype.canRedo = function () {
    return this.editor.graph.isEditing() || this.editor.undoManager.canRedo();
};

/**
 * Returns the URL for a copy of this editor with no state.
 */
EditorUi.prototype.canUndo = function () {
    return this.editor.graph.isEditing() || this.editor.undoManager.canUndo();
};

/**
 * Places a loaded graph after the diagram pane has reached its final size.
 */
EditorUi.prototype.initializeGraphView = function () {
    if (this.isMobileSplitterLayout() || mxClient.IS_TOUCH) {
        this.resetGraphView();
    } else {
        const graph = this.editor.graph;
        const bounds = graph.getGraphBounds();
        const width = bounds.width * graph.view.scale;
        const height = bounds.height * graph.view.scale;
        this.resetCells();
        graph.container.scrollTop = Math.floor(Math.max(0, bounds.y - Math.max(20, (graph.container.clientHeight - height) / 4)));
        graph.container.scrollLeft = Math.floor(Math.max(0, bounds.x - Math.max(0, (graph.container.clientWidth - width) / 10)));
    }
};

/**
 * Restores graph overlays and resets the initial viewport.
 */
EditorUi.prototype.resetGraphView = function () {
    const graph = this.editor.graph;
    this.resetCells();

    if (mxUtils.hasScrollbars(graph.container)) {
        this.resetScrollbars();
    } else {
        graph.center();
    }
};

/**
 * Resets the state of the scrollbars.
 */
EditorUi.prototype.resetScrollbars = function () {
    const graph = this.editor.graph;

    if (mxUtils.hasScrollbars(graph.container)) {
        const bounds = graph.getGraphBounds();
        const scrollTop = bounds.y - Math.max(0, graph.container.clientHeight - bounds.height) / 2;
        const scrollLeft = bounds.x - Math.max(0, graph.container.clientWidth - bounds.width) / 2;
        graph.container.scrollTop = Number.isFinite(scrollTop) ? Math.max(0, Math.floor(scrollTop)) : 0;
        graph.container.scrollLeft = Number.isFinite(scrollLeft) ? Math.max(0, Math.floor(scrollLeft)) : 0;
    }
};

EditorUi.prototype.resetScrollbarsTopLeft = function () {
    const graph = this.editor.graph;

    if (mxUtils.hasScrollbars(graph.container)) {
        const bounds = graph.getGraphBounds();
        const width = bounds.width * graph.view.scale;
        const height = bounds.height * graph.view.scale;
        graph.container.scrollTop = Math.floor(Math.max(0, bounds.y - Math.max(20, (graph.container.clientHeight - height) / 5)));
        graph.container.scrollLeft = Math.floor(Math.max(0, bounds.x - Math.max(0, (graph.container.clientWidth - width) / 8)));
    }
};

EditorUi.prototype.restoreMapViewport = function (viewport) {
    const graph = this.editor.graph;
    graph.view.scaleAndTranslate(viewport.scale, viewport.translateX, viewport.translateY);
    graph.sizeDidChange();
    graph.container.scrollLeft = viewport.scrollLeft + (graph.view.translate.x - viewport.translateX) * viewport.scale;
    graph.container.scrollTop = viewport.scrollTop + (graph.view.translate.y - viewport.translateY) * viewport.scale;
};

/**
 * Resets the state of the cells.
 */
EditorUi.prototype.resetCells = function () {
    const graph = this.editor.graph;

    const children = graph.model.getChildren(graph.model.getChildAt(graph.model.getRoot(), 0));
    children.forEach(cell => {
        if (graph.model.isEdge(cell) && cell.isConditional()) {
            graph.addCellOverlay(cell, graph.conditionsOverlay());
        }
    });
};

/**
 * Updates the states of the given undo/redo items.
 */
EditorUi.prototype.addUndoListener = function () {
    const undo = this.actions.get('undo');
    const redo = this.actions.get('redo');

    const undoMgr = this.editor.undoManager;

    const undoListener = mxUtils.bind(this, function () {
        undo.setEnabled(this.canUndo());
        redo.setEnabled(this.canRedo());
    });

    undoMgr.addListener(mxEvent.ADD, undoListener);
    undoMgr.addListener(mxEvent.UNDO, undoListener);
    undoMgr.addListener(mxEvent.REDO, undoListener);
    undoMgr.addListener(mxEvent.CLEAR, undoListener);

    // Overrides cell editor to update action states
    const cellEditorStartEditing = this.editor.graph.cellEditor.startEditing;
    this.editor.graph.cellEditor.startEditing = function () {
        cellEditorStartEditing.apply(this, arguments);
        undoListener();
    };

    const cellEditorStopEditing = this.editor.graph.cellEditor.stopEditing;
    this.editor.graph.cellEditor.stopEditing = function (cell, trigger) {
        cellEditorStopEditing.apply(this, arguments);
        undoListener();
    };

    // Updates the button states once
    undoListener();
};

/**
 * Updates the states of the given toolbar items based on the selection.
 */
EditorUi.prototype.updateActionStates = function () {
    const graph = this.editor.graph;
    const selected = !graph.isSelectionEmpty();
    let vertexSelected = false;
    let edgeSelected = false;

    const cells = graph.getSelectionCells();

    if (cells != null) {
        for (let i = 0; i < cells.length; i++) {
            const cell = cells[i];

            if (graph.getModel().isEdge(cell)) {
                edgeSelected = true;
            }

            if (graph.getModel().isVertex(cell)) {
                vertexSelected = true;
            }

            if (edgeSelected && vertexSelected) {
                break;
            }
        }
    }

    // Updates action states
    const actions = ['cut', 'copy', 'delete'];

    for (let i = 0; i < actions.length; i++) {
        this.actions.get(actions[i]).setEnabled(selected && graph.isCellDeletable(graph.getSelectionCell()));
    }

    // Updates menu states
    this.actions.get('home').setEnabled(graph.view.currentRoot != null);

    this.actions.get('save').setEnabled(graph.isEnabled() && this.editor.modified);

    const unlocked = graph.isEnabled() && !graph.isCellLocked(graph.getDefaultParent());
    this.actions.get('selectVertices').setEnabled(unlocked);
    this.actions.get('selectEdges').setEnabled(unlocked);
    this.actions.get('selectAll').setEnabled(unlocked);
    this.actions.get('selectNone').setEnabled(unlocked);

    this.updatePasteActionStates();
};

/**
 * Refreshes the viewport.
 */
EditorUi.prototype.refresh = function (sizeDidChange) {
    sizeDidChange = (sizeDidChange != null) ? sizeDidChange : true;

    let off = 0;

    this.diagramContainer.style.left = '0px';
    this.diagramContainer.style.top = this.toolbarContainer.clientHeight + 'px';
    this.diagramContainer.style.bottom = off + 'px';

    if (sizeDidChange) {
        this.editor.graph.sizeDidChange();
    }
};

EditorUi.prototype.getSplitterToolbarHeight = function () {
    return Math.max(
        this.toolbar?.container?.offsetHeight || 0,
        this.toolbarContainer?.offsetHeight || 0,
        this.toolbarContainer?.clientHeight || 0,
        this.toolbarHeight || 0
    );
};

EditorUi.prototype.getSplitterMinGraphHeight = function () {
    return this.getSplitterToolbarHeight() + this.splitMinDiagramHeight;
};

EditorUi.prototype.getSplitterFooterHeight = function () {
    const footer = document.querySelector('.page-footer, .ui.bottom.fixed.menu');
    return footer?.offsetHeight || 0;
};

EditorUi.prototype.getSplitterMaxGraphHeight = function () {
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || this.container.offsetHeight;
    const containerTop = this.container.getBoundingClientRect ? Math.max(0, this.container.getBoundingClientRect().top) : 0;
    const maxHeight = viewportHeight - containerTop - this.getSplitterFooterHeight() - this.splitSize - this.splitViewportMargin;

    return Math.max(this.getSplitterMinGraphHeight(), Math.floor(maxHeight));
};

EditorUi.prototype.isMobileSplitterLayout = function () {
    return typeof window.matchMedia === 'function' &&
        window.matchMedia('(pointer: coarse), (max-width: 767px)').matches;
};

EditorUi.prototype.getInitialMobileGraphPaneHeight = function () {
    const minHeight = this.getSplitterMinGraphHeight();
    const maxHeight = this.getSplitterMaxGraphHeight();

    return minHeight + (maxHeight - minHeight) * this.mobileInitialSplitRatio;
};

EditorUi.prototype.getGraphPaneHeight = function () {
    return this.container.offsetHeight || parseInt(this.container.style.height, 10) || this.getSplitterMinGraphHeight();
};

EditorUi.prototype.setGraphPaneHeight = function (height) {
    const boundedHeight = Math.max(
        this.getSplitterMinGraphHeight(),
        Math.min(this.getSplitterMaxGraphHeight(), Math.round(height))
    );

    this.container.style.height = boundedHeight + 'px';
    this.refresh(true);

    return boundedHeight;
};

EditorUi.prototype.handleSplitterResize = function (height) {
    return this.setGraphPaneHeight(height);
};

EditorUi.prototype.handleSplitterResizeEnd = function (height) {
    this.setGraphPaneHeight(height);
};

EditorUi.prototype.refreshSplitterAfterViewportChange = function () {
    if (!this.fullMode || !this.split) {
        this.refresh();
        return;
    }

    const currentHeight = this.getGraphPaneHeight();
    const maxHeight = this.getSplitterMaxGraphHeight();

    if (currentHeight > maxHeight) {
        this.setGraphPaneHeight(maxHeight);
    } else {
        this.refresh();
    }
};

EditorUi.prototype.createUi = function () {
    this.toolbar = new Toolbar(this, this.toolbarContainer);

    if (this.fullMode) {
        this.propertiesContainer = document.createElement('div');
        this.propertiesContainer.className = 'ui segment basic geSidebarContainer grapheditor-properties';
        this.container.insertAdjacentElement('afterend',
            this.propertiesContainer);
        this.sidebar = new Sidebar(this, this.propertiesContainer, this.currentWorkflowDefinition);

        document.querySelectorAll('#grapheditor-split').forEach(split => {
            if (split.parentNode != null) {
                split.parentNode.removeChild(split);
            }
        });

        this.split = document.createElement('div');
        this.split.className = 'ui segment fitted geVsplit';
        this.split.id = 'grapheditor-split';
        this.split.style.height = this.splitSize + 'px';
        this.container.insertAdjacentElement('afterend', this.split);

        const draggablePane = new DraggablePane(this.container, this.getSplitterMinGraphHeight(), {
            maxHeight: () => this.getSplitterMaxGraphHeight(),
            onResize: height => this.handleSplitterResize(height),
            onResizeEnd: height => this.handleSplitterResizeEnd(height)
        });

        if (this.isMobileSplitterLayout()) {
            this.setGraphPaneHeight(this.getInitialMobileGraphPaneHeight());
        }

        mxEvent.addGestureListeners(this.split, draggablePane.handleDragStart);

        this.destroyFunctions.push(() => {
            draggablePane.destroy();
            mxEvent.removeGestureListeners(this.split, draggablePane.handleDragStart);
        });
    }
};

/**
 * Saves the current graph.
 */
EditorUi.prototype.save = function () {
    if (!this.validatePendingResourceNames()) {
        document.getElementById('saving-wfd-success').classList.add('hidden');
        document.getElementById('saving-wfd-valid').classList.add('hidden');
        document.getElementById('saving-wfd').classList.add('hidden');
        document.getElementById('saving-wfd-error').classList.remove('hidden');
        return;
    }
    if (!this.editor.modified) {
        return;
    }

    const graph = this.editor.graph;
    if (graph.isEditing()) {
        graph.stopEditing();
    }

    this.editor.setModified(false);

    document.getElementById('saving-wfd-success').classList.add('hidden');
    document.getElementById('saving-wfd-error').classList.add('hidden');
    document.getElementById('saving-wfd-valid').classList.add('hidden');
    document.getElementById('saving-wfd-system-error').classList.add('hidden');
    document.getElementById('saving-wfd').classList.remove('hidden');

    if (!graph.validate()) {
        document.getElementById('saving-wfd').classList.add('hidden');
        document.getElementById('saving-wfd-error').classList.remove('hidden');
        graph.fireEvent(new mxEventObject(mxEvent.EDITING_STARTED));
        this.editor.setModified(true);
        return;
    }

    const model = graph.getModel();
    const workflow = {
        workflowDefinitionGeometry: toWorkflowDefinitionGeometry(model),
        workflowDefinition: toWorkflowDefinition(model),
        taskProcessors: toTaskProcessors(model),
        workflowStartPage: toWorkflowStartPage(model)
    };

    let method = 'POST';
    let apiEndpoint = '/api/workflow-definition-geometry';

    if (!isEmpty(this.currentWorkflowDefinition.id)) {
        method = 'PUT';
        apiEndpoint = `${apiEndpoint}/${this.currentWorkflowDefinition.id}`;
    } else if (!isEmpty(this.currentWorkflowDefinition.templateId)) {
        apiEndpoint = `${apiEndpoint}?template=${encodeURIComponent(this.currentWorkflowDefinition.templateId)}`;
        workflow.templateTaskNames = toTemplateTaskNames(model);
    }

    api(apiEndpoint, {
        method,
        headers: {
            'Content-Type': 'application/json'
        },
        body: stringifyJSON(workflow)
    }).then(idVersion => {
        document.getElementById('saving-wfd').classList.add('hidden');

        if (isEmpty(this.currentWorkflowDefinition.id)) {
            this.currentWorkflowDefinition.id = idVersion.id;
        }

        document.getElementById('saving-wfd-success').classList.remove('hidden');
        graph.fireEvent(new mxEventObject('graphSavedEvent'));
        graph.fireEvent(new mxEventObject(mxEvent.EDITING_STARTED));
    }).catch(error => {
        document.getElementById('saving-wfd').classList.add('hidden');

        if (this.showServerValidationErrors(error, workflow, model, graph)) {
            document.getElementById('saving-wfd-error').classList.remove('hidden');
            this.editor.setModified(true);
        } else {
            document.getElementById('saving-wfd-system-error').classList.remove('hidden');
            document.getElementById('saving-wfd-success').classList.add('hidden');
            document.getElementById('saving-wfd-error').classList.add('hidden');
            document.getElementById('saving-wfd').classList.add('hidden');

            graph.fireEvent(new mxEventObject(mxEvent.EDITING_STARTED));
            this.editor.setModified(true);

            throw error;
        }
    });
};

EditorUi.prototype.validate = function () {
    const graph = this.editor.graph;
    const requestId = this.validationRequestId = (this.validationRequestId || 0) + 1;
    document.getElementById('saving-wfd-valid').classList.add('hidden');
    document.getElementById('saving-wfd-error').classList.add('hidden');
    document.getElementById('saving-wfd-system-error').classList.add('hidden');
    document.getElementById('saving-wfd-success').classList.add('hidden');
    if (!this.validatePendingResourceNames() || !graph.validate()) {
        document.getElementById('saving-wfd-error').classList.remove('hidden');
        graph.fireEvent(new mxEventObject(mxEvent.EDITING_STARTED));
        return;
    }

    const model = graph.getModel();
    const workflowDefinition = toWorkflowDefinition(model);
    const serializedDefinition = stringifyJSON(workflowDefinition);
    const isCurrent = () => requestId === this.validationRequestId &&
        model === this.editor.graph.getModel() &&
        serializedDefinition === stringifyJSON(toWorkflowDefinition(model));
    api('/api/workflow-definitions/validate', {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: serializedDefinition
    }).then(() => {
        if (!isCurrent()) return;
        document.getElementById('saving-wfd-valid').classList.remove('hidden');
        graph.fireEvent(new mxEventObject(mxEvent.EDITING_STARTED));
    }).catch(error => {
        if (!isCurrent()) return;
        const validationError = error.fieldErrors ? {...error, fieldErrors: Object.fromEntries(
            Object.entries(error.fieldErrors).map(([field, detail]) => {
                const prefixed = field.startsWith('workflowDefinition.');
                const path = detail.path || [];
                const fullField = prefixed ? field : `workflowDefinition.${field}`;
                return [fullField, {
                    ...detail,
                    field: fullField,
                    path: path[0]?.name === 'workflowDefinition' ? path : [{name: 'workflowDefinition'}, ...path]
                }];
            }))} : error;
        if (this.showServerValidationErrors(validationError, {workflowDefinition}, model, graph)) {
            document.getElementById('saving-wfd-error').classList.remove('hidden');
        } else {
            document.getElementById('saving-wfd-system-error').classList.remove('hidden');
        }
    });
};

EditorUi.prototype.showServerValidationErrors = function (error, workflow, model, graph) {
    if (error.status !== 400 || !Object.hasOwn(error, 'fieldErrors')) {
        return false;
    }
    const workflowErrors = [];
    const fields = {};
    for (const field of ['name', 'version', 'timeoutSeconds', 'maxConcurrency']) {
        if (error.fieldErrors[`workflowDefinition.${field}`]) {
            fields[field] = error.fieldErrors[`workflowDefinition.${field}`].message;
        }
    }
    const failureWorkflowError = error.fieldErrors['workflowDefinition.failureWorkflow'] || error.fieldErrors['graph.failureWorkflow'];
    if (failureWorkflowError) {
        fields.failureWorkflow = failureWorkflowError.message;
    }
    const failureWorkflowVersionError = error.fieldErrors['workflowDefinition.failureWorkflowVersion'] || error.fieldErrors['graph.failureWorkflowVersion'];
    if (failureWorkflowVersionError) {
        fields.failureWorkflowVersion = failureWorkflowVersionError.message;
    }
    for (const field of ['tasks', 'startTask']) {
        if (error.fieldErrors[`workflowDefinition.${field}`]) {
            workflowErrors.push(error.fieldErrors[`workflowDefinition.${field}`].message);
        }
    }

    const {workflowErrors: resourceErrors, taskErrors, routeErrors} = mapServerFieldErrorsToGraphErrors({
        fieldErrors: error.fieldErrors, workflow, model, graph, mxResources
    });
    if (!isEmpty(fields) || workflowErrors.length > 0 || !isEmpty(resourceErrors)) {
        graph.setWorkflowErrors({
            message: workflowErrors,
            fields: {...fields, ...resourceErrors.fields}
        });
    }
    LOGGER.debug('taskErrors', taskErrors);
    LOGGER.debug('routeErrors', routeErrors);
    if (!isEmpty(taskErrors)) {
        graph.setTaskErrors(taskErrors);
    }
    if (!isEmpty(routeErrors)) {
        graph.setRouteErrors(routeErrors);
    }
    graph.fireEvent(new mxEventObject(mxEvent.EDITING_STARTED));
    return true;
};

EditorUi.prototype.validatePendingResourceNames = function () {
    return this.sidebar?.validatePendingResourceNames() ?? true;
};

// Alt+Shift+Keycode mapping to action
EditorUi.prototype.altShiftActions = {
    67: 'clearWaypoints', // Alt+Shift+C
    65: 'connectionArrows', // Alt+Shift+A
    80: 'connectionPoints', // Alt+Shift+P
    84: 'editTooltip', // Alt+Shift+T
};

/**
 * Creates the keyboard event handler for the current graph and history.
 */
EditorUi.prototype.createKeyHandler = function () {
    const editorUi = this;
    const graph = this.editor.graph;
    const keyHandler = new mxKeyHandler(graph);

    const isEventIgnored = keyHandler.isEventIgnored;
    keyHandler.isEventIgnored = function (evt) {
        // Handles undo/redo/ctrl+./,/u via action and allows ctrl+b/i only if editing value is HTML (except for FF and Safari)
        return (!this.isControlDown(evt) || mxEvent.isShiftDown(evt) || (evt.keyCode !== 90 && evt.keyCode !== 89 &&
                evt.keyCode !== 188 && evt.keyCode !== 190 && evt.keyCode !== 85)) && ((evt.keyCode !== 66 && evt.keyCode !== 73) ||
                !this.isControlDown(evt) || (!mxClient.IS_FF && !mxClient.IS_SF)) &&
            isEventIgnored.apply(this, arguments);
    };

    // Ignores graph enabled state but not chromeless state
    keyHandler.isEnabledForEvent = function (evt) {
        return (!mxEvent.isConsumed(evt) && this.isGraphEvent(evt) && this.isEnabled());
    };

    // Routes command-key to control-key on Mac
    keyHandler.isControlDown = function (evt) {
        return mxEvent.isControlDown(evt) || (mxClient.IS_MAC && evt.metaKey);
    };

    let queue = [];
    let thread = null;

    const nudge = (keyCode, stepSize, resize) => {
        queue.push(function () {
            if (!graph.isSelectionEmpty() && graph.isEnabled()) {
                stepSize = (stepSize != null) ? stepSize : 1;

                if (resize) {
                    // Resizes all selected vertices
                    graph.getModel().beginUpdate();
                    try {
                        const cells = graph.getSelectionCells();

                        for (let i = 0; i < cells.length; i++) {
                            if (graph.getModel().isVertex(cells[i]) && graph.isCellResizable(cells[i])) {
                                let geo = graph.getCellGeometry(cells[i]);

                                if (geo != null) {
                                    geo = geo.clone();

                                    if (keyCode === 37) {
                                        geo.width = Math.max(0, geo.width - stepSize);
                                    } else if (keyCode === 38) {
                                        geo.height = Math.max(0, geo.height - stepSize);
                                    } else if (keyCode === 39) {
                                        geo.width += stepSize;
                                    } else if (keyCode === 40) {
                                        geo.height += stepSize;
                                    }

                                    graph.getModel().setGeometry(cells[i], geo);
                                }
                            }
                        }
                    } finally {
                        graph.getModel().endUpdate();
                    }
                } else {
                    // Moves vertices up/down in a stack layout
                    const cell = graph.getSelectionCell();
                    const parent = graph.model.getParent(cell);
                    let layout = null;

                    if (graph.getSelectionCount() === 1 && graph.model.isVertex(cell) &&
                        graph.layoutManager != null && !graph.isCellLocked(cell)) {
                        layout = graph.layoutManager.getLayout(parent);
                    }

                    if (layout != null && layout.constructor === mxStackLayout) {
                        const index = parent.getIndex(cell);

                        if (keyCode === 37 || keyCode === 38) {
                            graph.model.add(parent, cell, Math.max(0, index - 1));
                        } else if (keyCode === 39 || keyCode === 40) {
                            graph.model.add(parent, cell, Math.min(graph.model.getChildCount(parent), index + 1));
                        }
                    } else {
                        let cells = graph.getMovableCells(graph.getSelectionCells());
                        const realCells = [];

                        for (let i = 0; i < cells.length; i++) {
                            const style = graph.getCurrentCellStyle(cells[i]);

                            if (mxUtils.getValue(style, 'part', '0') === '1') {
                                const parent = graph.model.getParent(cells[i]);

                                if (graph.model.isVertex(parent) && mxUtils.indexOf(cells, parent) < 0) {
                                    realCells.push(parent);
                                }
                            } else {
                                realCells.push(cells[i]);
                            }
                        }

                        if (realCells.length > 0) {
                            cells = realCells;
                            let dx = 0;
                            let dy = 0;

                            if (keyCode === 37) {
                                dx = -stepSize;
                            } else if (keyCode === 38) {
                                dy = -stepSize;
                            } else if (keyCode === 39) {
                                dx = stepSize;
                            } else if (keyCode === 40) {
                                dy = stepSize;
                            }

                            graph.moveCells(cells, dx, dy);
                        }
                    }
                }
            }
        });

        if (thread != null) {
            window.clearTimeout(thread);
        }

        thread = window.setTimeout(function () {
            if (queue.length > 0) {
                graph.getModel().beginUpdate();
                try {
                    for (let i = 0; i < queue.length; i++) {
                        queue[i]();
                    }

                    queue = [];
                } finally {
                    graph.getModel().endUpdate();
                }
                graph.scrollCellToVisible(graph.getSelectionCell());
            }
        }, 200);
    }

    // Overridden to handle special alt+shift+cursor keyboard shortcuts
    const directions = {
        37: mxConstants.DIRECTION_WEST, 38: mxConstants.DIRECTION_NORTH,
        39: mxConstants.DIRECTION_EAST, 40: mxConstants.DIRECTION_SOUTH
    };

    const keyHandlerGetFunction = keyHandler.getFunction;

    mxKeyHandler.prototype.getFunction = function (evt) {
        if (graph.isEnabled()) {
            if (mxEvent.isShiftDown(evt) && mxEvent.isAltDown(evt)) {
                const action = editorUi.actions.get(editorUi.altShiftActions[evt.keyCode]);

                if (action != null) {
                    return action.funct;
                }
            }

            if (directions[evt.keyCode] != null && !graph.isSelectionEmpty()) {
                if (this.isControlDown(evt)) {
                    return function () {
                        nudge(evt.keyCode, (mxEvent.isShiftDown(evt)) ? graph.gridSize : null, true);
                    };
                } else {
                    return function () {
                        nudge(evt.keyCode, (mxEvent.isShiftDown(evt)) ? graph.gridSize : null);
                    };
                }
            }
        }

        return keyHandlerGetFunction.apply(this, arguments);
    };

    // Binds keystrokes to actions
    keyHandler.bindAction = mxUtils.bind(this, function (code, control, key, shift) {
        const action = this.actions.get(key);

        if (action != null) {
            const f = function () {
                if (action.isEnabled()) {
                    action.funct();
                }
            };

            if (control) {
                if (shift) {
                    keyHandler.bindControlShiftKey(code, f);
                } else {
                    keyHandler.bindControlKey(code, f);
                }
            } else {
                if (shift) {
                    keyHandler.bindShiftKey(code, f);
                } else {
                    keyHandler.bindKey(code, f);
                }
            }
        }
    });

    const keyHandlerEscape = keyHandler.escape;
    keyHandler.escape = function (evt) {
        keyHandlerEscape.apply(this, arguments);
    };

    // Ignores enter keystroke. Remove this line if you want the
    // enter keystroke to stop editing. N, W, T are reserved.
    keyHandler.enter = function () {
    };

    keyHandler.bindKey(36, function () {
        graph.home();
    }); // Home
    keyHandler.bindKey(35, function () {
        graph.refresh();
    }); // End
    keyHandler.bindAction(107, true, 'zoomIn'); // Ctrl+Plus
    keyHandler.bindAction(109, true, 'zoomOut'); // Ctrl+Minus

    if (this.editor.editable) {
        keyHandler.bindControlKey(13, function () {
            if (graph.isEnabled()) {
                graph.setSelectionCells(graph.duplicateCells(graph.getSelectionCells(), false));
            }
        }); // Ctrl+Enter
        keyHandler.bindAction(8, false, 'delete'); // Backspace
        keyHandler.bindAction(46, false, 'delete'); // Delete
        keyHandler.bindAction(72, true, 'resetView'); // Ctrl+H
        keyHandler.bindAction(83, true, 'save'); // Ctrl+S
        keyHandler.bindAction(65, true, 'selectAll'); // Ctrl+A
        keyHandler.bindAction(65, true, 'selectNone', true); // Ctrl+A
        keyHandler.bindAction(73, true, 'selectVertices', true); // Ctrl+Shift+I
        keyHandler.bindAction(69, true, 'selectEdges', true); // Ctrl+Shift+E
        keyHandler.bindAction(68, true, 'duplicate'); // Ctrl+D
        keyHandler.bindAction(90, true, 'undo'); // Ctrl+Z
        keyHandler.bindAction(88, true, 'cut'); // Ctrl+X
        keyHandler.bindAction(67, true, 'copy'); // Ctrl+C
        keyHandler.bindAction(86, true, 'paste'); // Ctrl+V
        keyHandler.bindKey(13, function () {
            if (graph.isEnabled()) {
                graph.startEditingAtCell();
            }
        }); // Enter
        keyHandler.bindKey(113, function () {
            if (graph.isEnabled()) {
                graph.startEditingAtCell();
            }
        }); // F2
    }

    if (!mxClient.IS_WIN) {
        keyHandler.bindAction(90, true, 'redo', true); // Ctrl+Shift+Z
    } else {
        keyHandler.bindAction(89, true, 'redo'); // Ctrl+Y
    }

    return keyHandler;
};

/**
 * Creates the keyboard event handler for the current graph and history.
 */
EditorUi.prototype.destroy = function () {
    if (this.editor != null) {
        this.editor.destroy();
        this.editor = null;
    }

    if (this.toolbar != null) {
        this.toolbar.destroy();
        this.toolbar = null;
    }

    if (this.keyHandler != null) {
        this.keyHandler.destroy();
        this.keyHandler = null;
    }

    if (this.keydownHandler != null) {
        mxEvent.removeListener(document, 'keydown', this.keydownHandler);
        this.keydownHandler = null;
    }

    if (this.keyupHandler != null) {
        mxEvent.removeListener(document, 'keyup', this.keyupHandler);
        this.keyupHandler = null;
    }

    if (this.resizeHandler != null) {
        mxEvent.removeListener(window, 'resize', this.resizeHandler);
        this.resizeHandler = null;
    }

    if (this.gestureHandler != null) {
        mxEvent.removeGestureListeners(document, this.gestureHandler);
        this.gestureHandler = null;
    }

    if (this.scrollHandler != null) {
        mxEvent.removeListener(window, 'scroll', this.scrollHandler);
        this.scrollHandler = null;
    }

    if (this.beforeUnloadHandler != null) {
        mxEvent.removeListener(window, 'beforeunload', this.beforeUnloadHandler);
        this.beforeUnloadHandler = null;
    }

    if (this.destroyFunctions != null) {
        for (let i = 0; i < this.destroyFunctions.length; i++) {
            this.destroyFunctions[i]();
        }

        this.destroyFunctions = null;
    }

    const c = [this.toolbarContainer, this.diagramOutline, this.diagramContainer, this.split];

    if (this.propertiesContainer) {
        c.push(this.propertiesContainer);
    }

    for (let i = 0; i < c.length; i++) {
        if (c[i] != null && c[i].parentNode != null) {
            c[i].parentNode.removeChild(c[i]);
        }
    }
};
